import { useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'

import { SPORTS_PATH } from '../constants/sports'
import { useBoundStore } from '../store/store'
import type { Theme } from '../types/common'
import isSportsSection from '../utils/isSportsSection'
import normalizePathname from '../utils/normalizePathname'

const useTheme = () => {
  const theme = useBoundStore(state => state.common.theme)
  const casinoTheme = useBoundStore(state => state.common.casinoTheme)
  const sportsTheme = useBoundStore(state => state.common.sportsTheme)
  const themeSponsors = useBoundStore(state => state.common.themeSponsors)
  const setCasinoTheme = useBoundStore(state => state.common.setCasinoTheme)
  const setSportsTheme = useBoundStore(state => state.common.setSportsTheme)
  const setThemeSponsors = useBoundStore(
    state => state.common.setThemeSponsors,
  )
  const router = useRouter()
  const pathname = router.state.location.pathname
  const searchStr = router.state.location.searchStr

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const queryTheme = params.get('theme')
    const overrideTheme: Theme | undefined =
      queryTheme === 'light' || queryTheme === 'dark' ? queryTheme : undefined

    const isSponsors = pathname.startsWith('/sponsors')
    const isProfile = pathname.startsWith('/profile')
    // Route from the router, ?section marker from the live URL — the same
    // sources the inline check used.
    if (normalizePathname(pathname) === SPORTS_PATH) {
      setSportsTheme(overrideTheme ?? sportsTheme)
    } else if (isSportsSection(pathname)) {
      // Sports-marked profile pages still show our nav, so keep them light.
      setSportsTheme('light')
    } else if (isSponsors) {
      setThemeSponsors(overrideTheme ?? themeSponsors)
    } else if (!isProfile) {
      setCasinoTheme(overrideTheme ?? casinoTheme)
    }
  }, [pathname, searchStr])

  useEffect(() => {
    const els = [document.body, document.documentElement]
    if (theme === 'light') {
      els.forEach(el => {
        el.classList.remove('theme-dark')
        el.classList.add('theme-light')
      })
    } else if (theme === 'dark') {
      els.forEach(el => {
        el.classList.remove('theme-light')
        el.classList.add('theme-dark')
      })
    }
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'light' ? '#ffffff' : '#000000')
  }, [theme])
}

export default useTheme
